import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import {
  buildRole16FlatKey,
  getRole16Schema,
  getRole16ValidatingControls,
  isRole16CalculatedControl,
  isRole16Empty,
  isRole16StaticallyRequired,
  resolveRole16ColumnSuffixes,
  resolveRole16LayoutFlags,
  Role16Schema,
} from './role16-soci-before-tax.rules';
import { SHEET_KEYS } from '../../../models/sheet-keys';

/**
 * Pure validator for Role 16 (Statement of Comprehensive Income — BEFORE
 * TAX variant, role code 420000).
 *
 * Mirrors the editor's
 * role16-420000-statement-of-comprehensive-income-before-tax.component.ts
 * sheet-status surface. The component publishes its status via exactly ONE
 * site:
 *
 *   this.stateService.markSheetInitializedWithStatus(
 *     'statementOfComprehensiveIncomeBeforeTax', false
 *   );
 *
 * — `hasErrors` is hard-coded to `false` at init and NEVER re-published.
 * There is no `evaluateSheetStatus()` method, no post-init
 * `updateSheetLevelStatus()` call, and no valueChanges-driven status push.
 * `submitForm()` checks `form.invalid` for a toast but does not propagate
 * the result to state.
 *
 * Combined with the schema declaring `required: false` (implicit) on every
 * cell and providing NO `validators` array entries on any control, and
 * `buildForm()` creating every FormControl with an empty
 * `validators: ValidatorFn[] = []`, the editor's published `hasErrors` for
 * this sheet is STRUCTURALLY always `false` today.
 *
 * The pure validator therefore returns `hasErrors: false` deterministically
 * and carries the LB-001 fix-guard pattern (required detection from BOTH
 * `control.required === true` AND unconditional
 * `validators: [{ name: 'required' }]`) so any future schema edit that adds
 * a required cell gets picked up automatically — matching roles 11 / 13 /
 * 14 / 21 / 22 (LB-009 / LB-010 / LB-011 / LB-012 / LB-013 / LB-016 /
 * LB-017).
 *
 * ---- Calculations (T-010 sweep — LB-015 pattern) ------------------------
 *
 * Role 16's `calculateTotals()` references `ssmt:`-prefixed qnames
 * (RemeasurementOfDefinedBenefitPlansBeforeTax, ProfitForTheYearCI,
 * TotalItemsNotReclassifiedNetOfTax, etc.). The schema
 * (`role-420000.form.json`) declares every control with the same `ssmt:`
 * prefix. **No qname divergence observed** — Role 16 is confirmed CLEAN
 * for the T-010 sweep (same posture as role 12 per LB-018). This contrasts
 * with role 14 (LB-015), where the component references `ssmt:` qnames
 * against an `ifrs-full:` / `ifrs-smes:` schema and every calculated total
 * silently resolves to 0. See cross-cutting note on LB-015 for updated
 * sweep status.
 *
 * ---- Cross-sheet posture (Tier 4) ---------------------------------------
 *
 * Per SheetDependencyService, `scopeOfFiling` (Role 2) is listed as an
 * upstream of `statementOfComprehensiveIncomeBeforeTax` (column-visibility
 * driver) but NO reverse edge wires role 16 to role 15 or role 19. The
 * schema declares ZERO `validation.equality` blocks, so there is no
 * cross-sheet equality check to mirror here today.
 *
 * The validator still reads `context.crossSheetData?.get('scopeOfFiling')`
 * for column-visibility gating and defensively reads
 * `context.crossSheetData?.get('filingPrerequisites')` per the Tier 4
 * contract (the component's layout-change notifier watches both slices).
 * Both slices may be absent (parity-side mid-init, listing/dashboard
 * cache-miss path) — the validator tolerates absence by defaulting to the
 * conservative "single Current column" layout, matching the editor's
 * pre-mount initialiser values.
 *
 * If a future schema edit introduces a `validation.equality` block against
 * role 15 or role 19, the cross-sheet plumbing is already in place; only
 * the new check needs adding (plus an edge in SheetDependencyService).
 */

const _UNUSED_FUTURE_CROSS_SHEET_KEY = SHEET_KEYS.filingPrerequisites;

/**
 * Mirror the component's row-level "required field with no value → Failed"
 * branch, structurally identical to role 11 / role 14's check. Today this
 * loop is a no-op against the live schema (zero required controls) but the
 * LB-001 fix-guard ensures any future schema edit gets picked up
 * automatically.
 *
 * Calculated / readonly rows are skipped — their values are produced by
 * the component's `calculateTotals()` after valueChanges settle, and the
 * editor never marks them required. If a future schema edit ever sets
 * `required: true` on a calculated row, this skip preserves sensible
 * behaviour (the row would still receive a value via setValue, so flagging
 * it as "empty" would be misleading).
 */
function checkRequiredRowErrors(
  schema: Role16Schema,
  sheetData: Record<string, any>,
  context: SheetValidationContext
): Record<string, string> {
  const errors: Record<string, string> = {};

  const scopeSlice = context.crossSheetData?.get(SHEET_KEYS.scopeOfFiling);
  const { isConsolidated, hasPreviousYear } =
    resolveRole16LayoutFlags(scopeSlice);
  const suffixes = resolveRole16ColumnSuffixes(isConsolidated, hasPreviousYear);

  for (const control of getRole16ValidatingControls(schema)) {
    if (!isRole16StaticallyRequired(control)) continue;
    if (isRole16CalculatedControl(control)) continue;

    if (control.ui?.dualColumn) {
      // Mirror the component's row-as-satisfied-if-any-column-has-value
      // posture — a row counts as "Failed" only when ALL visible columns
      // are empty. This matches the way role 11 / role 14 check required
      // rows and lines up with the component's `hasValue` accumulator
      // pattern (when that pattern ever lands here).
      const keys = suffixes.map((suffix) => buildRole16FlatKey(control.qname, suffix));
      const anyFilled = keys.some((key) => !isRole16Empty(sheetData[key]));
      if (!anyFilled) {
        errors[control.qname] = `${control.qname} is required`;
      }
    } else {
      // Single-control rows (none in the current schema — every non-header
      // is dualColumn) live under the bare qname. Kept for forward
      // compatibility.
      if (isRole16Empty(sheetData[control.qname])) {
        errors[control.qname] = `${control.qname} is required`;
      }
    }
  }

  return errors;
}

export const role16SociBeforeTaxValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax,
  // Tier 4 per phase-2 plan: role 16 is the "before tax" alternative of
  // role 15 with its own calculation surface. The validator's output is
  // deterministic on sheetData + scopeOfFiling alone today (no equality
  // blocks, no required cells), but the Tier 4 classification stays so
  // the parity harness exercises both paths and a future schema edit
  // adding a genuine equality check against role 15 or role 19 plugs in
  // without re-tiering.
  tier: 4,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    // Defensive cross-sheet reads. `scopeOfFiling` drives column
    // visibility (read inside checkRequiredRowErrors via
    // resolveRole16LayoutFlags). `filingPrerequisites` is watched by the
    // component's layout-change notifier but has no role in the pure
    // path's Failed drivers today; the lookup exists so a future
    // `requiredIfCrossSheet` rule or schema edit referencing
    // filingPrerequisites.* plugs in without re-threading the interface.
    // Silenced "unused" signal documents the intent for readers.
    const _filingPrerequisites = context.crossSheetData?.get(
      _UNUSED_FUTURE_CROSS_SHEET_KEY
    );
    void _filingPrerequisites;

    const schema = getRole16Schema();

    const errors: Record<string, string> = {
      ...checkRequiredRowErrors(schema, sheetData, context),
    };

    return {
      hasErrors: Object.keys(errors).length > 0,
      // Developer Note (TODO): stub — `hasSuggestedChanges` is hard-coded false,
      // so this sheet's dropdown "*" can never light. When released as a live
      // monetary sheet, mirror role13: (1) recompute via
      // CalculationService.calculateTotals_<id> + hasCalculatedFieldMismatch(
      // context.extractedBaseline ?? sheetData, recomputed); (2) add its sheet key
      // to PURE_VERDICT_SHEETS; (3) call evaluateSheetStatus() in the component's
      // runAllValidations(); (4) add its sheet key to CALC_DIFF_SHEETS in
      // suggestion-capability.ts, or the "(*) Optional: Suggestions to refine
      // data" legend will stay hidden on this sheet even once its "*" can light.
      hasSuggestedChanges: false,
      errors,
    };
  },
};
